import React from 'react';
import ExternalTemplate from '../../../components/Templates/ExternalTemplate';
import Text from '../../../components/Typography/Text';
import Button from '../../../components/Buttons/Button';
import LinkButton from '../../../components/Buttons/LinkButton';
import TextField from '../../../components/Textfields/TextField';
import FormMessage from '../../../components/Messages/FormMessage';
import { monthNames } from '../../../lib/data/common';
import { validateEmail } from '../../../lib/validate/validate';
import { positiveInteger } from '../../../services/ConvertService';
import { Grid, Divider, CircularProgress } from '@material-ui/core';
import CodeField from '../../../components/Textfields/CodeField';
import { accountEmailAuth } from '../../../services/AuthService';
import { downloadStatementExternal } from '../../../services/StatementService';

const DEFAULT_MESSAGE = {
  type: '',
  value: '',
  isValidRequest: true,
};

const SUCCESS_MESSAGE = 'Statement is downloaded';

const DownloadMonthlyStatement = ({ match: { params } }) => {
  const [downloadRequest, setDownloadRequest] = React.useState({
    externalAuthId: 0,
    code: 0,
    month: '',
    year: 0,
    accountId: 0,
    email: '',
  });
  const [loading, setLoading] = React.useState(true);
  const [downloading, setDownloading] = React.useState(false);
  const [codeExpired, setCodeExpired] = React.useState(false);
  const [message, setMessage] = React.useState(DEFAULT_MESSAGE);

  const initRequest = async () => {
    try {
      const { year, month, accountId, email } = params;

      const downloadParam = {
        year,
        month: month.charAt(0).toUpperCase() + month.substring(1).toLowerCase(),
        accountId,
        email,
      };
      setDownloadRequest(downloadParam);

      if (!monthNames.includes(downloadParam.month)) {
        return setMessage({
          value: `Invalid Month '${downloadParam.month}'`,
          type: 'error',
        });
      }

      if (validateEmail(email)) {
        return setMessage({
          value: `Invalid Email '${email}'`,
          type: 'error',
        });
      }

      const yearInt = positiveInteger(year);
      if (yearInt < 2000 || yearInt > 9999) {
        return setMessage({
          value: `Invalid Year '${year}'`,
          type: 'error',
        });
      }
      downloadParam.year = yearInt;

      const accountIdInt = positiveInteger(accountId);
      if (!accountIdInt) {
        return setMessage({
          value: `Invalid Account Id '${accountId}'`,
          type: 'error',
        });
      }
      downloadParam.accountId = accountIdInt;

      try {
        const { externalAuthId } = await accountEmailAuth(accountId, email);
        downloadParam.externalAuthId = externalAuthId;
      } catch (error) {
        if (error?.code === 13) {
          return setMessage({
            value: `No account access found for email '${email}'`,
            type: 'error',
          });
        } else {
          console.error(error);
        }
      }
    } catch (error) {
      console.error(error);
      return setMessage({
        value: 'Oops! Sorry, something went wrong',
        type: 'error',
      });
    }
  };

  const init = async () => {
    setMessage(DEFAULT_MESSAGE);
    setLoading(true);
    await initRequest();
    const timeout = setTimeout(() => {
      setLoading(false);
      setCodeExpired(false);
      clearTimeout(timeout);
    }, 1000);
  };

  React.useEffect(() => {
    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ExternalTemplate>
      <form autoComplete="off">
        <Text mb={40} variant="h2" label="Monthly Statement" />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              disabled
              label="Email Address"
              type="text"
              value={downloadRequest.email}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              disabled
              label="Account ID"
              type="text"
              value={downloadRequest.accountId}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField disabled label="Year" type="number" value={downloadRequest.year} />
          </Grid>
          <Grid item xs={4}>
            <TextField disabled label="Month" type="text" value={downloadRequest.month} />
          </Grid>
        </Grid>
        <div className="text-divider">
          <Text
            variant="subtitle2"
            label={message.value ? '' : 'Code was sent to your email'}
          />
          <Divider />
        </div>
        {message.value && <FormMessage type={message.type} message={message.value} />}
        {loading && (
          <Grid item xs={12}>
            <CircularProgress color="secondary" />
          </Grid>
        )}
        {message.isValidRequest && message.value !== SUCCESS_MESSAGE && !loading && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CodeField
                autoFocus={true}
                max={5}
                minutes={2}
                startTimer
                setLoading={({ timer }) => {
                  setCodeExpired(!timer);
                }}
                label="Authentication Code"
                placeholder="5 Numeric Code"
                value={downloadRequest.code}
                onChange={(e) => {
                  setDownloadRequest({
                    ...downloadRequest,
                    code: e.currentTarget.value,
                  });
                }}
                helperText={
                  <LinkButton
                    tooltipTitle="New code will not be generated and send until the code expired"
                    type="button"
                    label="Resend code"
                    onClick={init}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                disabled={
                  message?.value?.isValidRequest ||
                  codeExpired ||
                  !downloadRequest.code ||
                  !downloadRequest.externalAuthId ||
                  downloading
                }
                onClick={async () => {
                  setDownloading(true);
                  try {
                    setMessage(DEFAULT_MESSAGE);
                    await downloadStatementExternal(downloadRequest);
                    setMessage({
                      value: SUCCESS_MESSAGE,
                      type: 'info',
                      isValidRequest: true,
                    });
                  } catch (error) {
                    if (error?.code === 16) {
                      setMessage({
                        value: 'Invalid Authentication Code',
                        type: 'error',
                        isValidRequest: true,
                      });
                    } else {
                      setMessage({
                        value: 'Unable to download statement. Please contact support.',
                        type: 'error',
                        isValidRequest: true,
                      });
                    }
                    console.error(error);
                  }
                  setDownloading(false);
                }}
                label="Download"
              />
            </Grid>
          </Grid>
        )}
      </form>
    </ExternalTemplate>
  );
};

export default DownloadMonthlyStatement;
